import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
	fetchObservations,
	selectObservationsByContext,
	selectObservationsByIds,
} from "./slice";
import { parseObservationFilters } from "./filters";
import { updateSearchParamsWithFilters } from "../filters/filters";
import { useInterval } from "../timing/use-interval";
import Layout from "../../ui/Layout";
import NavMain from "../../app/NavMain";
import ScrollablePage from "../../ui/ScrollablePage";
import Paper from "../../ui/Paper";
import ObservationsTable from "./ObservationsTable";
import DownloadCSVLink from "./DownloadCSVLink";
import { getObservationsBundleURL, getObservationsCSVURL } from "./api";
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner";

import ErrorMsg from "../../ui/ErrorMsg";

import TextFilter from "../filters/TextFilter";
import PropTypes from "prop-types";
import separateStrings from "../../strings/stringSplitter";
import LoadingSpinnerInline from "../../ui/LoadingSpinnerInline";
import { selectAuth } from "../iam/slice";
import {
	Action,
	ActionLink,
	Actions,
	Header,
	Overview,
} from "../../ui/PageHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";

const PAGE_SIZE = 50; // Default initial and subsequent page size
const LIVE_UPDATE_INTERVAL = 20000; // ms
const ONE_WEEK = 1000 * 60 * 60 * 24 * 7; // ms

/**
 * ObservationsList renders a page containing a filter- and sort-able table
 * of all observations matching the criteria in the current URL params.
 *
 * @returns {JSX.Element}
 * @constructor
 */
const ObservationsList = () => {
	const dispatch = useDispatch();
	const [updateLive, setUpdateLive] = useState(true);
	const { token } = useSelector(selectAuth);

	// we get any matching observations
	const observationMatches = useSelector((state) =>
		selectObservationsByContext(state, "root"),
	);
	// We get the full objects from the ids that match
	const observations = useSelector((state) =>
		selectObservationsByIds(state, observationMatches?.ids || []),
	);

	// Multi-selection behaviour
	const [selected, setSelected] = useState(new Set());

	// Parse parameters into filter object (which drives filters UI state)
	// Note: We are using the URL as the "state" here, and updates
	// perform a history replacement (so as not to mess up "back" button
	// behaviour too much - though we may want to apply a heuristic like
	// small changes replace history, big changes push history)
	const location = useLocation();
	const navigate = useNavigate();
	const urlParams = new URLSearchParams(location.search);
	const filter = parseObservationFilters(urlParams);
	updateSearchParamsWithFilters(urlParams, filter);

	useEffect(() => {
		dispatch(
			fetchObservations({
				filter,
				page: { offset: 0, limit: PAGE_SIZE },
				context: ["root"],
			}),
		);
		// FIXME: If we have selected items that are no longer on the actively-displayed
		// page, how do we clear them? Whilst retaining selection for ones that *are*
		// still on the page, otherwise you lose your selection every X seconds (annoying)
	}, [urlParams.toString()]);

	// How we apply a filter update. Filter state is entirely in the URL query parameters
	const setFilter = (filter = {}) => {
		// For now - whenever the user changes filters, re-set selection to avoid hiding
		// selected items (and thus making them non-un-selectable)
		setSelected(new Set());
		const updUrlParams = updateSearchParamsWithFilters(urlParams, filter);
		navigate({
			pathname: location.pathname,
			search: `?${updUrlParams}`,
		});
	};

	// How many more observations are available to load?
	const loadableCount = Math.min(
		PAGE_SIZE,
		observations &&
			observations.length &&
			observationMatches &&
			observationMatches.page &&
			observationMatches.page.total_count
			? observationMatches.page.total_count - observations.length
			: 50,
	);

	// Event handler for 'Show xxx more' button that was clicked
	const handleLoadMore = () => {
		const lastIndex = observations && observations.length;
		setUpdateLive(false); // To 'freeze' current view

		const page = {
			offset: lastIndex,
			limit: loadableCount,
		};
		dispatch(fetchObservations({ filter, page, context: ["root"] }));
	};

	// Poll at an interval, based on whether "Update live" is checked
	// As a hack, for non-live updates, set a long (but non-infinite, which breaks) interval, e.g. a week
	useInterval(
		() => {
			dispatch(
				fetchObservations({
					filter,
					page: { offset: 0, limit: PAGE_SIZE },
					context: ["root"],
				}),
			);
		},
		updateLive ? LIVE_UPDATE_INTERVAL : ONE_WEEK,
		false,
	);

	// Every time 'Update live' is toggled to true, fetch observations
	const handleToggleUpdateLive = (e) => {
		setUpdateLive(e.target.checked);
		if (e.target.checked) {
			const updUrlParams = updateSearchParamsWithFilters(urlParams, filter);
			navigate({
				pathname: location.pathname,
				search: `?${updUrlParams}`,
			});
		}
	};

	const handleTogggleIncludeArchived = (e) => {
		setFilter({
			...filter,
			include_archived: e.target.checked ? true : false,
		});
	};

	return (
		<Layout>
			<NavMain />
			{observations.length === 0 &&
				observationMatches?._loadStatus === "loading" && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
			<ScrollablePage>
				<Helmet>
					<title>Observations - Astrosite</title>
				</Helmet>

				<Header>
					<Overview>
						<h1>Observations</h1>
						{observations.length > 0 && (
							<p>
								Showing <strong>{observationMatches?.page?.end_index}</strong>
								{observationMatches &&
									observationMatches.page &&
									observationMatches.page.total_count && (
										<span>
											{" "}
											of{" "}
											<strong className="count total_count">
												{observationMatches.page.total_count}
											</strong>
										</span>
									)}{" "}
								observations
								{observationMatches?._loadStatus === "loading" && (
									<LoadingSpinnerInline />
								)}
							</p>
						)}
						{observationMatches?._loadStatus === "rejected" && (
							<ErrorMsg
								msg={"There was a problem fetching observation"}
								showIcon={true}
							/>
						)}{" "}
					</Overview>
					<Actions>
						<Action>
							<label>
								<span>Update live</span>
								<input
									type="checkbox"
									checked={updateLive}
									onChange={handleToggleUpdateLive}
								/>
							</label>
						</Action>
						<Action>
							<label>
								<span>Include archived</span>
								<input
									type="checkbox"
									onChange={handleTogggleIncludeArchived}
								/>
							</label>
						</Action>
						<Action>
							<DownloadCSVLink
								displayCount={observationMatches?.page?.total_count || 0}
								filter={filter}
								generateURL={() => getObservationsCSVURL(filter, token)}
							/>
						</Action>
						{selected.size > 0 && selected.size <= 50 && (
							<>
								<Action>
									<ActionLink
										href={getObservationsBundleURL("event_stream", "csv", [
											...selected,
										])}
										target="_blank"
									>
										<span>
											Selected {selected.size} <strong>event_stream</strong> as
											CSV
										</span>
										<FontAwesomeIcon icon={faDownload} />
									</ActionLink>
								</Action>
								<Action>
									<ActionLink
										href={getObservationsBundleURL("points_3d.json", "tdm", [
											...selected,
										])}
										target="_blank"
									>
										<span>
											Selected {selected.size} <strong>tracks</strong> as ICD
											(TDM)
										</span>
										<FontAwesomeIcon icon={faDownload} />
									</ActionLink>
								</Action>
							</>
						)}
					</Actions>
				</Header>
				<Paper>
					<ObservationsIdFilter filter={filter} setFilter={setFilter} />
					{/* Render the observations as a table */}
					{observations && (
						<ObservationsTable
							observations={observations}
							filter={filter}
							setFilter={setFilter}
							selected={selected}
							setSelected={setSelected}
						/>
					)}
					<footer className="actions">
						{observationMatches &&
							observationMatches.page &&
							observations.length > 0 &&
							observations.length !== observationMatches.page.total_count && (
								<>
									<button
										onClick={handleLoadMore}
										disabled={observationMatches?._loadStatus === "loading"}
									>
										{observationMatches?._loadStatus === "loading"
											? "Loading..."
											: `Show ${loadableCount} more`}
									</button>
								</>
							)}
					</footer>
				</Paper>
			</ScrollablePage>
		</Layout>
	);
};

/**
 * Split the string bases on the supported separators (,:; ), remove any duplicates and return as an array
 * @param idsString
 * @returns {T[]}
 */
function splitAndRemoveDuplicates(idsString) {
	// Step 1: Split the comma-separated string into an array of IDs
	// Step 2: Convert to a set to remove duplicates then convert back
	return Array.from(
		new Set(separateStrings(idsString, ",.;: ").filter((id) => id != "")),
	);
}

const ObservationsIdFilter = ({ filter = {}, setFilter }) => {
	const updateParentFilter = (filter = {}) => {
		setFilter(filter);
	};

	return (
		<div>
			<TextFilter
				placeholder={"Filter by comma seperated list of observation ids"}
				filterByName="observation_ids"
				filter={filter}
				setFilter={updateParentFilter}
				active={true}
				transform={(v) => splitAndRemoveDuplicates(v).join(",")}
			/>
		</div>
	);
};

ObservationsIdFilter.propTypes = {
	filter: PropTypes.object,
	setFilter: PropTypes.func,
};

export default ObservationsList;
